/**
 * Chance-corrected agreement: Cohen's kappa (two raters) and Fleiss' kappa (many).
 *
 * ## Cohen
 *
 * One routine covers simple and weighted kappa, because simple kappa is weighted kappa
 * with identity weights. Weights are agreement weights on equally spaced category
 * scores: linear (Cicchetti–Allison) w = 1 − |i − j|/(k − 1), quadratic (Fleiss–Cohen)
 * w = 1 − (i − j)²/(k − 1)². The large-sample variances are Fleiss, Cohen and Everitt
 * (1969) — the non-null one for the confidence interval, the null one for the z test —
 * which is what SAS PROC FREQ prints; the tests pin both against its documented output.
 *
 * ## Fleiss
 *
 * Fleiss (1971) for N subjects each rated by the same number of raters n. The standard
 * error is Fleiss, Nee and Landis (1979), which is valid for testing κ = 0 only; it is
 * not a basis for a confidence interval, and the calculator says so.
 */

import { normalQuantile, normalUpper } from "@/lib/stats/distributions";

export type KappaWeighting = "none" | "linear" | "quadratic";

export function kappaWeights(k: number, weighting: KappaWeighting): number[][] {
  return Array.from({ length: k }, (_, i) =>
    Array.from({ length: k }, (__, j) => {
      if (weighting === "none" || k < 2) {
        return i === j ? 1 : 0;
      }
      const distance = Math.abs(i - j) / (k - 1);
      return weighting === "linear" ? 1 - distance : 1 - distance ** 2;
    })
  );
}

export interface KappaResult {
  n: number;
  categories: number;
  /** Observed (weighted) agreement p_o. */
  observed: number;
  /** Chance-expected (weighted) agreement p_e. */
  expected: number;
  kappa: number;
  se: number;
  lower: number;
  upper: number;
  /** Standard error under H₀: κ = 0. */
  seNull: number;
  z: number;
  p: number;
  /** The largest κ these margins allow (simple kappa only). */
  maxKappa: number | null;
}

interface Margins {
  n: number;
  proportions: number[][];
  rows: number[];
  columns: number[];
}

function margins(table: readonly (readonly number[])[]): Margins | null {
  const k = table.length;
  if (k < 2 || table.some((row) => row.length !== k)) {
    return null;
  }
  let n = 0;
  for (const row of table) {
    for (const cell of row) {
      n += cell;
    }
  }
  if (!(n > 0)) {
    return null;
  }
  const proportions = table.map((row) => row.map((cell) => cell / n));
  const rows = proportions.map((row) => row.reduce((sum, p) => sum + p, 0));
  const columns = proportions[0].map((_, j) =>
    proportions.reduce((sum, row) => sum + row[j], 0)
  );
  return { n, proportions, rows, columns };
}

function weightedSums(m: Margins, w: number[][]) {
  const k = m.rows.length;
  let observed = 0;
  let expected = 0;
  for (let i = 0; i < k; i++) {
    for (let j = 0; j < k; j++) {
      observed += w[i][j] * m.proportions[i][j];
      expected += w[i][j] * m.rows[i] * m.columns[j];
    }
  }
  // w̄ᵢ. = Σⱼ p.ⱼ wᵢⱼ and w̄.ⱼ = Σᵢ pᵢ. wᵢⱼ
  const rowMeans = w.map((row) =>
    row.reduce((sum, weight, j) => sum + weight * m.columns[j], 0)
  );
  const columnMeans = w[0].map((_, j) =>
    w.reduce((sum, row, i) => sum + row[j] * m.rows[i], 0)
  );
  return { observed, expected, rowMeans, columnMeans };
}

function kappaVariances(
  m: Margins,
  w: number[][],
  kappa: number,
  sums: ReturnType<typeof weightedSums>
): { variance: number; nullVariance: number } {
  const k = m.rows.length;
  let a = 0;
  let a0 = 0;
  for (let i = 0; i < k; i++) {
    for (let j = 0; j < k; j++) {
      const shift = sums.rowMeans[i] + sums.columnMeans[j];
      a += m.proportions[i][j] * (w[i][j] - shift * (1 - kappa)) ** 2;
      a0 += m.rows[i] * m.columns[j] * (w[i][j] - shift) ** 2;
    }
  }
  const scale = (1 - sums.expected) ** 2 * m.n;
  return {
    variance: (a - (kappa - sums.expected * (1 - kappa)) ** 2) / scale,
    nullVariance: (a0 - sums.expected ** 2) / scale,
  };
}

function maximumKappa(m: Margins, expected: number): number {
  let best = 0;
  for (let i = 0; i < m.rows.length; i++) {
    best += Math.min(m.rows[i], m.columns[i]);
  }
  return (best - expected) / (1 - expected);
}

/**
 * Kappa from a k × k table of counts (rows = rater A, columns = rater B). Null when the
 * table is not square, is empty, or every rating sits in one category — chance agreement
 * is then 100% and kappa is 0/0.
 */
export function cohenKappa(
  table: readonly (readonly number[])[],
  weighting: KappaWeighting = "none",
  level = 0.95
): KappaResult | null {
  const m = margins(table);
  if (!m) {
    return null;
  }
  const w = kappaWeights(m.rows.length, weighting);
  const sums = weightedSums(m, w);
  if (!(1 - sums.expected > 1e-12)) {
    return null;
  }
  const kappa = (sums.observed - sums.expected) / (1 - sums.expected);
  const { variance, nullVariance } = kappaVariances(m, w, kappa, sums);
  const se = Math.sqrt(Math.max(0, variance));
  const seNull = Math.sqrt(Math.max(0, nullVariance));
  const zCritical = normalQuantile(1 - (1 - level) / 2);
  const z = seNull > 0 ? kappa / seNull : Number.NaN;
  return {
    n: m.n,
    categories: m.rows.length,
    observed: sums.observed,
    expected: sums.expected,
    kappa,
    se,
    lower: kappa - zCritical * se,
    upper: kappa + zCritical * se,
    seNull,
    z,
    p: Number.isFinite(z)
      ? Math.min(1, 2 * normalUpper(Math.abs(z)))
      : Number.NaN,
    maxKappa: weighting === "none" ? maximumKappa(m, sums.expected) : null,
  };
}

/** Landis & Koch's (1977) labels — benchmarks they called arbitrary themselves. */
export type AgreementBand =
  | "poor"
  | "slight"
  | "fair"
  | "moderate"
  | "substantial"
  | "almostPerfect";

export function landisKoch(kappa: number): AgreementBand {
  if (kappa < 0) {
    return "poor";
  }
  if (kappa <= 0.2) {
    return "slight";
  }
  if (kappa <= 0.4) {
    return "fair";
  }
  if (kappa <= 0.6) {
    return "moderate";
  }
  if (kappa <= 0.8) {
    return "substantial";
  }
  return "almostPerfect";
}

/**
 * Categories in a sensible default order: numeric when every label is a number (so
 * ratings 1–5 sort as numbers, which weighted kappa needs), otherwise in order of first
 * appearance.
 */
export function defaultCategoryOrder(labels: Iterable<string>): string[] {
  const unique: string[] = [];
  const seen = new Set<string>();
  for (const label of labels) {
    if (label !== "" && !seen.has(label)) {
      seen.add(label);
      unique.push(label);
    }
  }
  const numeric = unique.map(Number);
  if (unique.length > 0 && numeric.every((value) => Number.isFinite(value))) {
    return [...unique].sort((a, b) => Number(a) - Number(b));
  }
  return unique;
}

/**
 * Put the categories in the reader's order: those they listed first, then any others in
 * the default order. Unknown names in the list are ignored.
 */
export function orderCategories(
  found: readonly string[],
  preferred: readonly string[]
): string[] {
  const present = new Set(found);
  const ordered = preferred.filter(
    (label, index) => present.has(label) && preferred.indexOf(label) === index
  );
  const placed = new Set(ordered);
  return [...ordered, ...found.filter((label) => !placed.has(label))];
}

/** A k × k table from paired labels (rows = first rater). */
export function crossTabulate(
  pairs: readonly (readonly [string, string])[],
  categories: readonly string[]
): number[][] {
  const index = new Map(categories.map((label, i) => [label, i]));
  const table = categories.map(() => categories.map(() => 0));
  for (const [a, b] of pairs) {
    const i = index.get(a);
    const j = index.get(b);
    if (i !== undefined && j !== undefined) {
      table[i][j] += 1;
    }
  }
  return table;
}

/* ------------------------------------------------------------------ Fleiss */

export interface FleissResult {
  subjects: number;
  raters: number;
  categories: number;
  /** Overall share of ratings in each category, pⱼ. */
  proportions: number[];
  /** Agreement within each subject, Pᵢ. */
  subjectAgreement: number[];
  meanAgreement: number;
  chanceAgreement: number;
  kappa: number;
  /** Fleiss, Nee & Landis (1979) standard error under H₀: κ = 0. */
  seNull: number;
  z: number;
  p: number;
  /** κⱼ for each category; NaN when nobody (or everybody) used it. */
  categoryKappa: number[];
  categorySeNull: number;
  categoryZ: number[];
  categoryP: number[];
}

/**
 * The number of raters, when every row has the same total; otherwise null and the rows
 * (0-based) whose totals differ from the most common total.
 */
export function ratersPerSubject(counts: readonly (readonly number[])[]): {
  raters: number | null;
  mismatched: number[];
} {
  const totals = counts.map((row) =>
    row.reduce((sum, value) => sum + value, 0)
  );
  const tally = new Map<number, number>();
  for (const total of totals) {
    tally.set(total, (tally.get(total) ?? 0) + 1);
  }
  let common = 0;
  let best = -1;
  for (const [total, times] of tally) {
    if (times > best) {
      best = times;
      common = total;
    }
  }
  const mismatched = totals.flatMap((total, i) =>
    total === common ? [] : [i]
  );
  return { raters: mismatched.length === 0 ? common : null, mismatched };
}

function twoSided(z: number): number {
  return Number.isFinite(z)
    ? Math.min(1, 2 * normalUpper(Math.abs(z)))
    : Number.NaN;
}

function fleissCategoryKappas(
  counts: readonly (readonly number[])[],
  proportions: readonly number[],
  raters: number
): number[] {
  const subjects = counts.length;
  return proportions.map((p, j) => {
    const spread = p * (1 - p);
    if (!(spread > 0)) {
      return Number.NaN;
    }
    let disagreement = 0;
    for (const row of counts) {
      disagreement += row[j] * (raters - row[j]);
    }
    return 1 - disagreement / (subjects * raters * (raters - 1) * spread);
  });
}

/** Fleiss' kappa from a subjects × categories table of counts. */
export function fleissKappa(
  counts: readonly (readonly number[])[]
): FleissResult | null {
  const subjects = counts.length;
  const k = counts[0]?.length ?? 0;
  const { raters } = ratersPerSubject(counts);
  if (subjects < 1 || k < 2 || raters === null || raters < 2) {
    return null;
  }
  const totalRatings = subjects * raters;
  const proportions = Array.from({ length: k }, (_, j) => {
    let sum = 0;
    for (const row of counts) {
      sum += row[j];
    }
    return sum / totalRatings;
  });
  const subjectAgreement = counts.map((row) => {
    const squares = row.reduce((sum, value) => sum + value * value, 0);
    return (squares - raters) / (raters * (raters - 1));
  });
  const meanAgreement =
    subjectAgreement.reduce((sum, value) => sum + value, 0) / subjects;
  const chanceAgreement = proportions.reduce((sum, p) => sum + p * p, 0);
  if (!(1 - chanceAgreement > 1e-12)) {
    return null;
  }
  const kappa = (meanAgreement - chanceAgreement) / (1 - chanceAgreement);
  const spread = proportions.reduce((sum, p) => sum + p * (1 - p), 0);
  const skew = proportions.reduce(
    (sum, p) => sum + p * (1 - p) * (1 - 2 * p),
    0
  );
  const seNull =
    (Math.SQRT2 / (spread * Math.sqrt(totalRatings * (raters - 1)))) *
    Math.sqrt(Math.max(0, spread * spread - skew));
  const z = seNull > 0 ? kappa / seNull : Number.NaN;
  const categoryKappa = fleissCategoryKappas(counts, proportions, raters);
  const categorySeNull = Math.sqrt(2 / (totalRatings * (raters - 1)));
  const categoryZ = categoryKappa.map((value) => value / categorySeNull);
  return {
    subjects,
    raters,
    categories: k,
    proportions,
    subjectAgreement,
    meanAgreement,
    chanceAgreement,
    kappa,
    seNull,
    z,
    p: twoSided(z),
    categoryKappa,
    categorySeNull,
    categoryZ,
    categoryP: categoryZ.map(twoSided),
  };
}

/** Counts per category from one row of labels per subject. */
export function countsFromRatings(
  rows: readonly (readonly string[])[],
  categories: readonly string[]
): number[][] {
  const index = new Map(categories.map((label, i) => [label, i]));
  return rows.map((row) => {
    const counts = categories.map(() => 0);
    for (const label of row) {
      const j = index.get(label);
      if (j !== undefined) {
        counts[j] += 1;
      }
    }
    return counts;
  });
}
